import React, { useEffect, useLayoutEffect, useRef } from "react";
import cn from "classnames";
import { CloseOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import styles from "./styles.module.scss";
import { ReactComponent as LogoIcon } from "../../assets/appinstalls-mark.svg";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as UsersIcon } from "./assets/users.svg";
import { ReactComponent as OrdersIcon } from "./assets/orders.svg";
import { ReactComponent as ModerationIcon } from "./assets/moderation.svg";
import { ReactComponent as PlannerIcon } from "./assets/planner.svg";
import { ReactComponent as ImportIcon } from "./assets/import.svg";
import { ReactComponent as BalanceIcon } from "./assets/balance.svg";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import { ReactComponent as LogsIcon } from "./assets/logs.svg";
import { ReactComponent as LogoutIcon } from "./assets/logout.svg";
import { useUser } from "../../context/UserContext";
import {
  Flip,
  gsap,
  prefersReducedMotion,
  SETTLE,
  useGSAP,
} from "../../motion/gsap";

// Every item's indicator shares one Flip id: the one that lights up next
// takes over from where the last one stood.
const INDICATOR_FLIP_ID = "sidebar-indicator";

type Tone =
  | "blue"
  | "warning"
  | "cyan"
  | "purple"
  | "orange"
  | "sky"
  | "gray"
  | "indigo";

type SidebarItem = {
  to: string;
  label: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  tone: Tone;
  superAdminOnly?: boolean;
};

type SidebarSection = {
  key: string;
  title: string;
  items: SidebarItem[];
};

// Navigation is data; markup and tokens follow the client's DS Sidebar
// (Figma node 470:66847): captioned groups split by a hairline, a 40px
// tinted icon box per item, a brand circle and indicator for the active one.
const SECTIONS: SidebarSection[] = [
  {
    key: "clients",
    title: "Clients",
    items: [
      { to: "/users", label: "Users", Icon: UsersIcon, tone: "blue" },
      // USDT top-ups to the Ledger and their AML decisions.
      {
        to: "/crypto-payments",
        label: "Crypto payments",
        Icon: BalanceIcon,
        tone: "cyan",
      },
    ],
  },
  {
    key: "orders",
    title: "Orders",
    items: [
      {
        to: "/campaigns",
        label: "Campaigns",
        Icon: OrdersIcon,
        tone: "warning",
      },
      {
        to: "/moderation",
        label: "Moderation",
        Icon: ModerationIcon,
        tone: "indigo",
      },
      {
        to: "/campaigns-super-admin",
        label: "Campaigns 2.0",
        Icon: PlannerIcon,
        tone: "cyan",
        superAdminOnly: true,
      },
      {
        to: "/idfa-reports",
        label: "IDFA reports",
        Icon: ImportIcon,
        tone: "orange",
      },
    ],
  },
  {
    key: "settings",
    title: "Settings",
    items: [
      {
        to: "/price-settings",
        label: "Price settings",
        Icon: BalanceIcon,
        tone: "purple",
      },
      {
        to: "/superadmin-panel",
        label: "Superadmin panel",
        Icon: ShieldIcon,
        tone: "sky",
        superAdminOnly: true,
      },
      // The client's Activity tone: the same kind of page, about admins.
      {
        to: "/logs",
        label: "Logs",
        Icon: LogsIcon,
        tone: "gray",
        superAdminOnly: true,
      },
    ],
  },
];

const LOGOUT: SidebarItem = {
  to: "/logout",
  label: "Log out",
  Icon: LogoutIcon,
  tone: "gray",
};

interface SidebarProps {
  opened: boolean;
  onToggle: () => void;
  /** On a phone the menu is a drawer over the page instead of a column. */
  drawer?: { open: boolean; onClose: () => void };
}

// "Users" is also the fallback route ("/") and owns a user's page
// (/user/:id); the rest match their path and its sub-paths.
const isItemActive = (to: string, pathname: string) =>
  to === "/users"
    ? pathname === "/" || pathname.startsWith("/user")
    : pathname === to || pathname.startsWith(`${to}/`);

const Item: React.FC<{
  item: SidebarItem;
  opened: boolean;
  active: boolean;
  onClick?: () => void;
}> = ({ item, opened, active, onClick }) => (
  <Link
    to={item.to}
    className={cn(styles.item, { [styles.active]: active })}
    aria-current={active ? "page" : undefined}
    title={!opened ? item.label : undefined}
    onClick={onClick}
  >
    <span
      className={styles.indicator}
      data-flip-id={INDICATOR_FLIP_ID}
      aria-hidden
    />
    <span className={cn(styles.iconBox, styles[`tone-${item.tone}`])}>
      <item.Icon />
    </span>
    {opened && <span className={styles.label}>{item.label}</span>}
  </Link>
);

const Sidebar: React.FC<SidebarProps> = ({ opened, onToggle, drawer }) => {
  const { user } = useUser();
  const { pathname } = useLocation();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const drawerOpen = Boolean(drawer?.open);
  const onClose = drawer?.onClose;
  const navRef = useRef<HTMLElement>(null);
  const indicatorState = useRef<Flip.FlipState | null>(null);
  const wasOpened = useRef(opened);

  const activeIndicator = () =>
    navRef.current?.querySelector<HTMLElement>(
      `.${styles.active} .${styles.indicator}`,
    ) ?? null;

  // Where the indicator stands the moment an item is clicked, before the
  // route changes. Back/forward moves it without a glide: nothing recorded.
  const recordIndicator = (event: React.MouseEvent) => {
    if (!(event.target as HTMLElement).closest("a")) return;
    const indicator = activeIndicator();
    indicatorState.current = indicator ? Flip.getState(indicator) : null;
  };

  useLayoutEffect(() => {
    const state = indicatorState.current;
    indicatorState.current = null;
    const indicator = activeIndicator();
    if (!state || !indicator || prefersReducedMotion()) return;
    Flip.from(state, {
      targets: indicator,
      duration: 0.45,
      ease: "power3.inOut",
    });
  }, [pathname]);

  // The menu settles in item by item: on a desktop when the app opens, on a
  // phone each time the drawer comes out.
  useGSAP(
    () => {
      if (prefersReducedMotion() || (drawer && !drawerOpen)) return;
      gsap.from(`.${styles.item}, .${styles.account}`, {
        x: -10,
        autoAlpha: 0,
        duration: 0.4,
        stagger: 0.03,
        delay: drawer ? 0.08 : 0,
        clearProps: SETTLE,
      });
    },
    { dependencies: [drawerOpen], scope: navRef },
  );

  // Expanding the collapsed menu: the labels appear once there is room.
  useGSAP(
    () => {
      const expanded = opened && !wasOpened.current;
      wasOpened.current = opened;
      if (!expanded || prefersReducedMotion()) return;
      gsap.from(
        `.${styles.label}, .${styles.sectionTitle}, .${styles.logoLabel}, .${styles.accountText}`,
        {
          x: -6,
          autoAlpha: 0,
          duration: 0.3,
          stagger: 0.015,
          delay: 0.08,
          clearProps: SETTLE,
        },
      );
    },
    { dependencies: [opened], scope: navRef },
  );

  // While the drawer is out the page under it stays put, and Escape puts
  // the drawer away.
  useEffect(() => {
    if (!drawerOpen || !onClose) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen, onClose]);

  const sections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((i) => !i.superAdminOnly || isSuperAdmin),
  })).filter((section) => section.items.length > 0);

  const nav = (
    <nav
      ref={navRef}
      onClickCapture={recordIndicator}
      className={cn(styles.sidebar, {
        [styles.collapsed]: !opened,
        [styles.drawer]: Boolean(drawer),
        [styles.drawerOpen]: drawerOpen,
      })}
      aria-hidden={drawer && !drawerOpen ? true : undefined}
    >
      <div className={styles.inner}>
        <div className={styles.logoRow}>
          <LogoIcon className={styles.logo} />
          {opened && <span className={styles.logoLabel}>Admin</span>}
          {drawer && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={drawer.onClose}
              aria-label="Close menu"
            >
              <CloseOutlined />
            </button>
          )}
        </div>

        <div className={styles.list}>
          {sections.map((section, index) => (
            <React.Fragment key={section.key}>
              {index > 0 && <hr className={styles.divider} />}
              <div className={styles.section}>
                {opened && (
                  <span className={styles.sectionTitle}>{section.title}</span>
                )}
                {section.items.map((item) => (
                  <Item
                    key={item.to}
                    item={item}
                    opened={opened}
                    active={isItemActive(item.to, pathname)}
                    onClick={onClose}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className={styles.footer}>
          {user && (
            <div
              className={styles.account}
              title={!opened ? user.name || user.email : undefined}
            >
              <span className={styles.avatar} aria-hidden>
                {(user.name || user.email || "?")
                  .trim()
                  .charAt(0)
                  .toUpperCase()}
              </span>
              {opened && (
                <span className={styles.accountText}>
                  <span className={styles.accountName}>
                    {user.name || user.email}
                  </span>
                  <span className={styles.accountRole}>
                    {isSuperAdmin ? "Super admin" : "Admin"}
                  </span>
                </span>
              )}
            </div>
          )}
          <Item item={LOGOUT} opened={opened} active={false} />
        </div>
      </div>

      {!drawer && (
        <button
          type="button"
          className={styles.toggle}
          onClick={onToggle}
          aria-label={opened ? "Collapse menu" : "Expand menu"}
        >
          <ArrowLeftIcon />
        </button>
      )}
    </nav>
  );

  if (!drawer) return nav;

  return (
    <>
      <div
        className={cn(styles.backdrop, {
          [styles.backdropVisible]: drawerOpen,
        })}
        onClick={drawer.onClose}
        aria-hidden
      />
      {nav}
    </>
  );
};

export default Sidebar;
